import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Tenant, TenantDetail } from '../types/index.js';
import { tenants as tenantsApi } from '../api/client.js';
import { useAuth } from './AuthContext.js';

interface TenantState {
  tenants: Tenant[];
  activeTenant: TenantDetail | null;
  activeTenantId: string | null;
  loading: boolean;
  hasServer: boolean;
  error: string | null;
  selectTenant: (id: string) => Promise<boolean>;
  refreshTenants: () => Promise<void>;
  refreshActiveTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantState | null>(null);

const STORAGE_KEY = 'subfi_active_tenant';

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tenantList, setTenantList] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenant] = useState<TenantDetail | null>(null);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasServer, setHasServer] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTenantDetail = useCallback(async (id: string): Promise<boolean> => {
    try {
      console.log('[tenant] loading detail for id:', id);
      const response = await tenantsApi.get(id);
      console.log('[tenant] GET /tenants/:id response:', JSON.stringify(response, null, 2).substring(0, 500));
      const { tenant } = response;
      if (!tenant) {
        console.error('[tenant] response missing "tenant" key. Full response:', response);
        setError('Unexpected response format from server');
        return false;
      }
      console.log('[tenant] loaded detail for:', tenant.name, '| embedConfig:', tenant.embedConfig ? 'present' : 'NULL', '| affiliateEnabled:', tenant.affiliateEnabled);
      setActiveTenant(tenant);
      setActiveTenantId(id);
      localStorage.setItem(STORAGE_KEY, id);
      return true;
    } catch (err) {
      console.error('[tenant] failed to load detail for', id, err);
      setError('Failed to load server details');
      return false;
    }
  }, []);

  const refreshTenants = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      console.log('[tenant] fetching tenant list...');
      const response = await tenantsApi.list();
      console.log('[tenant] GET /tenants response:', JSON.stringify(response, null, 2).substring(0, 500));
      const { tenants } = response;
      if (!tenants) {
        console.error('[tenant] response missing "tenants" key. Full response:', response);
        setError('Unexpected response format from server');
        setLoading(false);
        return;
      }
      console.log('[server] loaded', tenants.length, 'servers:', tenants.map(t => t.name).join(', '));
      setTenantList(tenants);
      setHasServer(tenants.length > 0);

      if (tenants.length === 0) {
        setActiveTenant(null);
        setActiveTenantId(null);
        setLoading(false);
        return;
      }

      // restore saved selection or pick first
      const saved = localStorage.getItem(STORAGE_KEY);
      const target = tenants.find((t) => t.id === saved) ? saved! : tenants[0]!.id;
      await loadTenantDetail(target);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      const status = (err as Record<string, unknown>)?.status;
      console.error('[tenant] failed to load tenant list:', { msg, status, err });
      setError(`Failed to load servers${status ? ` (${status})` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [user, loadTenantDetail]);

  const selectTenant = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      const ok = await loadTenantDetail(id);
      setLoading(false);
      return ok;
    },
    [loadTenantDetail],
  );

  const refreshActiveTenant = useCallback(async () => {
    if (activeTenantId) {
      await loadTenantDetail(activeTenantId);
    }
  }, [activeTenantId, loadTenantDetail]);

  useEffect(() => {
    if (user) void refreshTenants();
  }, [user, refreshTenants]);

  return (
    <TenantContext.Provider
      value={{
        tenants: tenantList,
        activeTenant,
        activeTenantId,
        loading,
        hasServer,
        error,
        selectTenant,
        refreshTenants,
        refreshActiveTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantState {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
